import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
const router = createRouter({
    history: createWebHistory(),
    routes: [
        { path: "/login", name: "login", component: () => import("@/views/LoginView.vue") },
        {
            path: "/set-password",
            name: "set-password",
            component: () => import("@/views/SetPasswordView.vue"),
        },
        {
            path: "/admin",
            component: () => import("@/views/admin/AdminLayout.vue"),
            meta: { requiresAuth: true, requiresAdmin: true },
            children: [
                // V1.16.3: /admin → Dashboard (was redirect to /admin/users)
                { path: "", name: "admin-dashboard", component: () => import("@/views/admin/DashboardView.vue") },
                { path: "users", component: () => import("@/views/admin/UsersView.vue") },
                { path: "plans", component: () => import("@/views/admin/PlansView.vue") },
                { path: "templates", component: () => import("@/views/admin/TemplatesView.vue") },
                // V1.16: unified instances view + per-kind provision pages
                { path: "instances", name: "admin-instances", component: () => import("@/views/admin/InstancesView.vue") },
                // V1.20: list pages /admin/lxc and /admin/vm retired; unified at /admin/instances.
                // Per-kind list pages kept for provision modals; reachable from
                // InstancesView's "+ Provision LXC / + Provision VM" buttons.
                { path: "lxc/:id(\\d+)", component: () => import("@/views/admin/LxcDetailView.vue"), props: true },
                {
                    path: "lxc/:id(\\d+)/console",
                    name: "admin-console",
                    component: () => import("@/views/me/ConsoleView.vue"),
                    props: (route) => ({ vmid: route.params.id, source: "admin", kind: "lxc" }),
                },
                // V1.20: list pages /admin/lxc and /admin/vm retired; unified at /admin/instances.
                { path: "vm/:id(\\d+)", component: () => import("@/views/admin/VmDetailView.vue"), props: true },
                {
                    path: "vm/:id(\\d+)/console",
                    name: "admin-vm-console",
                    component: () => import("@/views/me/ConsoleView.vue"),
                    props: (route) => ({ vmid: route.params.id, source: "admin", kind: "vm" }),
                },
                { path: "profile", name: "admin-profile", component: () => import("@/views/me/ProfileView.vue") },
                { path: "setup", name: "admin-setup", component: () => import("@/views/admin/SetupWizardView.vue") },
                { path: "audit", component: () => import("@/views/admin/AuditView.vue") },
                { path: "ip-pool", component: () => import("@/views/admin/IpPoolsView.vue") },
            ],
        },
        {
            path: "/",
            component: () => import("@/views/me/MeLayout.vue"),
            meta: { requiresAuth: true },
            children: [
                // V1.16: unified instances view replaces "My LXCs" + "My VMs" lists
                { path: "", name: "my-instances", component: () => import("@/views/me/MyInstancesView.vue") },
                {
                    path: "lxc/:vmid",
                    name: "my-lxc-detail",
                    component: () => import("@/views/me/MyLxcDetailView.vue"),
                    props: true,
                },
                {
                    path: "lxc/:vmid/console",
                    name: "my-console",
                    component: () => import("@/views/me/ConsoleView.vue"),
                    props: (route) => ({ vmid: route.params.vmid, source: "me", kind: "lxc" }),
                },
                // Back-compat: /vm used to be the user's VM list
                { path: "vm", redirect: "/" },
                {
                    path: "vm/:vmid",
                    name: "my-vm-detail",
                    component: () => import("@/views/me/MyVmDetailView.vue"),
                    props: true,
                },
                {
                    path: "vm/:vmid/console",
                    name: "my-vm-console",
                    component: () => import("@/views/me/ConsoleView.vue"),
                    props: (route) => ({ vmid: route.params.vmid, source: "me", kind: "vm" }),
                },
                { path: "profile", name: "profile", component: () => import("@/views/me/ProfileView.vue") },
                {
                    path: "me/ssh-keys",
                    name: "ssh-keys",
                    component: () => import("@/views/me/SshKeysView.vue"),
                },
            ],
        },
        {
            path: "/:pathMatch(.*)*",
            name: "not-found",
            component: () => import("@/views/NotFoundView.vue"),
        },
    ],
});
router.beforeEach((to) => {
    const auth = useAuthStore();
    if (to.meta.requiresAuth && !auth.isAuthenticated) {
        return { path: "/login", query: { redirect: to.fullPath } };
    }
    if (to.meta.requiresAdmin && !auth.isAdmin) {
        return { path: "/" };
    }
    if (to.path === "/login" && auth.isAuthenticated) {
        return auth.isAdmin ? "/admin" : "/";
    }
    return true;
});
export default router;
